import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { FooterComponent } from './shared/components/footer/footer.component';
import { NavigationBarComponent } from './shared/components/navigation-bar/navigation-bar.component';
import { ClientService } from './shared/services/client/client.service';
import { MetaTagsService } from './shared/services/meta-tags/meta-tags.service';
import { SharedService } from './shared/services/shared-service/shared.service';
import { TranslateService } from './shared/services/translation/translate.service';
import { AuthService } from './shared/services/auth/auth.service';
import { toObservable } from '@angular/core/rxjs-interop';
declare const gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less'],
    imports: [
        CommonModule,
        NavigationBarComponent,
        RouterModule,
        FooterComponent,
        MatProgressSpinnerModule,
        ReactiveFormsModule,
    ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy{
  favIcon: any = this.document.querySelector('#appIcon');
  appleTouchIcon: any = this.document.querySelector('#appAppleTouch');
  selectedLanguage: string | undefined;
  clientLoaded = false;
  loader = false;
  currentUrl = '';
  private isBrowser: boolean;
  clientRequestDebounce: any;
  subs: Subscription = new Subscription();
  private authDataObservable = toObservable(this.authService.authData);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public translate: TranslateService,
    public clientService: ClientService,
    private router: Router,
    private Meta: MetaTagsService,
    private authService: AuthService,
    private sharedService: SharedService,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    
    this.isBrowser = isPlatformBrowser(platformId);

    this.authDataObservable.subscribe((data) => {
      if (data && !data.hasAccess && !data?.hasCompletedSignup || data?.userStatus === 'PENDING' || data?.userStatus === 'REJECTED') {
         this.router.navigateByUrl(`/${this.translate.language}/validate`).then();
      }
      this.clientLoaded = true;
    });

  }

  async ngOnInit() {
    this.getClientObject('default');
  }

  async ngAfterViewInit() {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.currentUrl = e.url;
        if (e.url.includes('/home')) {
          if (this.clientService.client === null) {
            this.getClientObject('NEWVALUE');
          }
        }
      }
    });

    setTimeout(() => {
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          )
        )
        .subscribe((event) => {
          if (this.isBrowser) {
            if (typeof gtag !== 'undefined' && this.isBrowser) {
              gtag('event', 'page_view', {
                page_path: event.urlAfterRedirects,
              });
            }
          }
        });
    }, 400);

  }

  getBrowserLang() {
    this.selectedLanguage = this.translate.language;
  }

  async getClientObject(type?: string) {
    this.selectedLanguage = this.translate.language;
    this.setFavIcon(this.clientService.client?.clientFavIcon, this.clientService.client?.clientAppleTouch);
    this.loadGoogleScripts();
    if (this.clientService.client.clientName === 'KB Auto') {
      this.loader = true;
      if (
        this.currentUrl.includes('/for-sale') ||
        this.currentUrl.includes('/contact') ||
        this.currentUrl.includes('/terms')
      ) {
        this.loader = false;
      }
      if (type === 'NEWVALUE') {
        if (this.router.url.includes('/home')) {
          if (this.clientService.client.clientName === 'KB Auto') {
            let kbAutoPageMap = this.sharedService.kbAutoPageObject.find(
              (item: any) => item.lang === this.selectedLanguage
            );
            let item = kbAutoPageMap?.pages.find(
              (page: any) => page.key === 'home'
            );
            if (item) {
              let a = this.document.createElement('a');
              a.href = item.link;
              a.click();
            }
          }
        }
      } else {
        this.router.events.subscribe((e) => {
          if (e instanceof NavigationEnd) {
            if (e.url.includes('/home')) {
              if (this.clientService.client.clientName === 'KB Auto') {
                let kbAutoPageMap =
                  this.sharedService.kbAutoPageObject.find(
                    (item: any) => item.lang === this.selectedLanguage
                  );
                let item = kbAutoPageMap?.pages.find(
                  (page: any) => page.key === 'home'
                );
                if (item) {
                  let a = this.document.createElement('a');
                  a.href = item.link;
                  a.click();
                }
              }
            }
          }
        });
      }
    }
    this.loader = false;
  }

  loadGoogleScripts() {
    this.Meta.set({
      'google-site-verification': `${this.clientService.client.gtmTrackingId}`,
    });

    if (this.sharedService.isBrowser()) {
      const url = `https://www.googletagmanager.com/gtag/js?id=${this.clientService.client.trackingId}`;
      let nodeOne = document.createElement('script');
      nodeOne.src = url;
      nodeOne.async = true;
      document.getElementsByTagName('head')[0].appendChild(nodeOne);

      let nodeTwo = document.createElement('script');
      nodeTwo.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${this.clientService.client.trackingId}');`;
      document.getElementsByTagName('head')[0].appendChild(nodeTwo);

      let nodeThree = document.createElement('script');
      nodeThree.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${this.clientService.client.gtmTrackingId}');`;
      document.getElementsByTagName('head')[0].appendChild(nodeThree);

      let nodeFour = document.createElement('noscript');
      nodeFour.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.clientService.client.gtmTrackingId}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.getElementsByTagName('body')[0].appendChild(nodeFour);

      let nodeFive = document.createElement('script');
      nodeFive.async = true;
      nodeFive.innerHTML = `(function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
      (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
        m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
      })(window,document,'script','https://www.google-analytics.com/analytics.js','ga');

      ga('create', '${this.clientService.client.trackingId}', 'auto');
      ga('send', 'pageview');`;
      document.getElementsByTagName('head')[0].appendChild(nodeFive);
    }
  }

  setFavIcon(favIcon: any, appleTouchIcon: any) {
    this.favIcon.href = favIcon ? favIcon : '../favicon.ico';
    this.appleTouchIcon.href = appleTouchIcon
      ? appleTouchIcon
      : '../assets/images/icons/machineric-apple-touch.png';
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
